import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Snackbar,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ContentCopy as ContentCopyIcon,
  DeleteOutline as DeleteIcon,
  Tune as TuneIcon,
  QrCode2 as QrCodeIcon,
  Groups as GroupsIcon,
  Label as LabelIcon,
  LinkOff as LinkOffIcon,
  MoreVert as MoreVertIcon,
  OpenInNew as OpenInNewIcon,
  People as PeopleIcon,
  ThreeDRotation as ThreeDIcon,
  PersonSearch as PersonSearchIcon,
  Visibility as VisibilityIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import { api, ShareLink } from '../../services/api';
import DistinctiveDialog, { DistinctiveValues } from '../../dialogs/others/DistinctiveDialog';
import { DistinctiveTemplate, SHARE_LINK_UNLOCK_TEMPLATES } from '../../distinctive/distinctiveTemplates';
import {
  logSharableLinkAction,
  logSharableLinkCreateOpened,
  logSharableLinksViewed,
} from '../../services/analytics';
import { CreateShareLinkDialog } from './CreateShareLinkDialog';
import { ShareLinkLeadsDrawer } from './ShareLinkLeadsDrawer';
import { ShareLinkToClientsSheet } from './ShareLinkToClientsSheet';
import { useAppWindow } from './useAppWindow';
import { SharableLinksIntro } from './SharableLinksIntro';
import { SharableLinksStats } from './SharableLinksStats';
import {
  copyText,
  formatRelative,
  SHARE_LINK_TYPE_LABELS,
  shareLinkUrl,
  whatsAppShareUrl,
} from './shareLinkFormat';

/** Warning shown before a link is removed from view. */
function RemoveWarning({ link }: { link: ShareLink }) {
  return (
    <Alert severity="warning" sx={{ mt: 2 }}>
      <strong>Removing can't be undone.</strong> The link leaves this list for good, and its {link.viewCount}{' '}
      {link.viewCount === 1 ? 'view' : 'views'} and {link.leadCount} {link.leadCount === 1 ? 'lead' : 'leads'} stop
      counting in your stats and in leader statistics. The leads themselves stay in Super Connector.
    </Alert>
  );
}

/**
 * "My Shareable Links" tab: links the agent has shared with consumers, their
 * reach, and the leads they produced. Leads themselves live in Super
 * Connector; this tab links through to them.
 *
 * Any agent can create a 3D Map link. The extra blocks (Price Promo,
 * Valuation, …) stay locked until the agent has shared a "Novel Lead
 * Generation" distinctive; tapping a locked block prompts one of those designs
 * at random.
 */
export function MySharableLinksTab({ distinctiveValues, autoOpenIntro = false, onIntroAutoOpened }: {
  /** Agent / agency footer lines for the unlock design (same as the Distinctives gallery). */
  distinctiveValues: DistinctiveValues;
  /** Roll the launch page down as soon as the tab shows (the ?redirect=sharable-links deep link). */
  autoOpenIntro?: boolean;
  onIntroAutoOpened?: () => void;
}) {
  const [links, setLinks] = useState<ShareLink[] | null>(null);
  // Extra blocks unlocked? null until the list loads; a failed load leaves them open (the server still gates them).
  const [unlocked, setUnlocked] = useState<boolean | null>(null);
  const [unlockTemplate, setUnlockTemplate] = useState<DistinctiveTemplate | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [leadsFor, setLeadsFor] = useState<ShareLink | null>(null);
  const [qrFor, setQrFor] = useState<ShareLink | null>(null);
  const [menu, setMenu] = useState<{ anchor: HTMLElement; link: ShareLink } | null>(null);
  // WhatsApp button: send to Super Connector clients, or pick a contact in WhatsApp.
  const [waMenu, setWaMenu] = useState<{ anchor: HTMLElement; link: ShareLink } | null>(null);
  const [sendToClientsFor, setSendToClientsFor] = useState<ShareLink | null>(null);
  const [revoking, setRevoking] = useState<ShareLink | null>(null);
  const [revokeBusy, setRevokeBusy] = useState(false);
  // Revoke dialog: also remove the link from view in the same step.
  const [alsoRemove, setAlsoRemove] = useState(false);
  const [removing, setRemoving] = useState<ShareLink | null>(null);
  const [removeBusy, setRemoveBusy] = useState(false);
  const [configFor, setConfigFor] = useState<ShareLink | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const appWindow = useAppWindow();
  const [loading, setLoading] = useState(false);
  const [introCovered, setIntroCovered] = useState(false);
  const viewLogged = useRef(false);
  // Read once on mount: the deep-link flag is cleared as soon as the intro starts rolling.
  const viewSource = useRef<'tab' | 'deep_link'>(autoOpenIntro ? 'deep_link' : 'tab');

  const load = useCallback(() => {
    setError(null);
    setLoading(true);
    api.getShareLinks()
      .then((res) => {
        setLinks(res.links);
        setUnlocked(res.unlocked);
        if (viewLogged.current) return;
        viewLogged.current = true;
        logSharableLinksViewed({
          source: viewSource.current,
          linkCount: res.links.length,
          liveLinkCount: res.links.filter((l) => !l.revokedAt).length,
          totalViews: res.links.reduce((sum, l) => sum + l.viewCount, 0),
          totalLeads: res.links.reduce((sum, l) => sum + l.leadCount, 0),
        });
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const promptUnlock = () => {
    setUnlockTemplate(SHARE_LINK_UNLOCK_TEMPLATES[Math.floor(Math.random() * SHARE_LINK_UNLOCK_TEMPLATES.length)]);
  };

  const openCreate = (source: 'stats' | 'empty_state' | 'intro') => {
    logSharableLinkCreateOpened({ source });
    setCreateOpen(true);
  };

  const handleRevoke = async () => {
    if (!revoking) return;
    setRevokeBusy(true);
    try {
      if (alsoRemove) {
        await api.removeShareLink(revoking.id);
        logSharableLinkAction({ action: 'revoke', link: revoking, source: 'menu', leadCount: revoking.leadCount });
        logSharableLinkAction({ action: 'remove', link: revoking, source: 'menu', leadCount: revoking.leadCount });
        setLinks((prev) => prev?.filter((l) => l.id !== revoking.id) ?? prev);
        setToast('Link revoked and removed');
      } else {
        const res = await api.revokeShareLink(revoking.id);
        logSharableLinkAction({ action: 'revoke', link: revoking, source: 'menu', leadCount: revoking.leadCount });
        setLinks((prev) => prev?.map((l) => (l.id === revoking.id ? { ...l, revokedAt: res.revokedAt } : l)) ?? prev);
        setToast('Link revoked');
      }
      setRevoking(null);
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'Failed to revoke');
    } finally {
      setRevokeBusy(false);
    }
  };

  const handleRemove = async () => {
    if (!removing) return;
    setRemoveBusy(true);
    try {
      await api.removeShareLink(removing.id);
      logSharableLinkAction({ action: 'remove', link: removing, source: 'menu', leadCount: removing.leadCount });
      setLinks((prev) => prev?.filter((l) => l.id !== removing.id) ?? prev);
      setToast('Link removed');
      setRemoving(null);
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'Failed to remove');
    } finally {
      setRemoveBusy(false);
    }
  };

  const openConfig = (link: ShareLink) => {
    logSharableLinkAction({ action: 'config_open', link, source: 'menu' });
    setConfigFor(link);
  };

  // Opens the buyer site already unlocked — no WhatsApp code, no lead, no open counted.
  const handlePreview = async (link: ShareLink) => {
    logSharableLinkAction({ action: 'preview', link, source: 'menu' });
    const win = appWindow.open(`Preview · ${link.projectName || 'Share link'}`, `share-preview-${link.id}`);
    try {
      const { url } = await api.getShareLinkPreviewUrl(link.id);
      win.navigate(url);
    } catch (err) {
      win.close();
      setToast(err instanceof Error ? err.message : 'Failed to open preview');
    }
  };

  const totals = (links ?? []).reduce(
    (acc, l) => ({ views: acc.views + l.viewCount, loggedInViews: acc.loggedInViews + (l.loggedInViewCount ?? 0), leads: acc.leads + l.leadCount }),
    { views: 0, loggedInViews: 0, leads: 0 },
  );

  return (
    <Box
      sx={{ px: { xs: 1.5, sm: 3 }, py: { xs: 2, sm: 3 } }}
    >
      <SharableLinksIntro onCreateLink={() => openCreate('intro')} onCoveredChange={setIntroCovered}
        autoOpen={autoOpenIntro} onAutoOpened={onIntroAutoOpened} />

      {/* Behind the open curtain the list is dropped, so there is nothing to scroll to. */}
      <Box sx={{ display: introCovered ? 'none' : 'block' }}>
      <SharableLinksStats
        totals={links && links.length > 0
          ? { links: links.filter((l) => !l.revokedAt).length, opens: totals.views, loggedInViews: totals.loggedInViews, leads: totals.leads }
          : null}
        loading={loading}
        onRefresh={load}
        onCreate={() => openCreate('stats')}
      />

      {error && <Alert severity="error" action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>{error}</Alert>}
      {!links && !error && <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>}

      {links && links.length === 0 && (
        <Box sx={{
          textAlign: 'center', py: 8, px: 3, borderRadius: 3,
          border: '1.5px dashed rgba(var(--color-text-rgb), 0.18)',
        }}>
          <ThreeDIcon sx={{ fontSize: 48, color: 'var(--color-accent)' }} />
          <Typography sx={{ fontWeight: 700, fontSize: 18, color: 'var(--color-text-strong)', mt: 1 }}>
            Share your first 3D map
          </Typography>
          <Typography variant="body2" sx={{ color: 'var(--color-text-muted)', mt: 1, maxWidth: 420, mx: 'auto' }}>
            Pick a launch, add a note, and send the link on WhatsApp. Buyers explore the site, stacks and sunlight —
            you get their verified number.
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => openCreate('empty_state')}
            sx={{ mt: 3, bgcolor: 'var(--color-accent)', fontWeight: 700 }}>
            Create a link
          </Button>
        </Box>
      )}

      {links && links.length > 0 && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', lg: 'repeat(3, 1fr)' }, gap: 2 }}>
          {links.map((link) => {
            const revoked = !!link.revokedAt;
            return (
              <Box key={link.id} sx={{
                borderRadius: 3, overflow: 'hidden', bgcolor: 'var(--color-surface)',
                border: '1px solid rgba(var(--color-text-rgb), 0.1)',
                boxShadow: '0 1px 2px rgba(var(--color-shadow-rgb), 0.08)',
                opacity: revoked ? 0.6 : 1,
              }}>
                <Box sx={{ p: 2, display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <Box sx={{
                    width: 44, height: 44, borderRadius: 2, flexShrink: 0, display: 'grid', placeItems: 'center',
                    bgcolor: 'rgba(var(--color-accent-rgb), 0.12)', color: 'var(--color-accent)',
                  }}>
                    <ThreeDIcon />
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography sx={{ fontWeight: 700, color: 'var(--color-text-strong)' }} noWrap>
                        {link.projectName || link.projectId}
                      </Typography>
                      <Chip size="small" label={SHARE_LINK_TYPE_LABELS[link.linkType]}
                        sx={{ height: 20, fontSize: 11, bgcolor: 'rgba(var(--color-accent-rgb), 0.12)', color: 'var(--color-accent)', fontWeight: 600 }} />
                      {revoked && <Chip size="small" label="Revoked" sx={{ height: 20, fontSize: 11 }} />}
                      {link.connectorTag && (
                        <Tooltip title="Super Connector tag added to every lead from this link">
                          <Chip size="small" icon={<LabelIcon sx={{ fontSize: 14 }} />} label={link.connectorTag.name}
                            sx={{
                              height: 20, fontSize: 11, fontWeight: 600,
                              bgcolor: `${link.connectorTag.color || '#ed8936'}22`, color: link.connectorTag.color || '#ed8936',
                              '& .MuiChip-icon': { color: 'inherit' },
                            }} />
                        </Tooltip>
                      )}
                    </Box>
                    {link.description && (
                      <Typography variant="body2" sx={{ color: 'var(--color-text-muted)', mt: 0.25,
                        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {link.description}
                      </Typography>
                    )}
                    <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', display: 'block', mt: 0.5 }} noWrap>
                      {shareLinkUrl(link)} · created {formatRelative(link.createdAt)}
                    </Typography>
                  </Box>
                  <IconButton size="small" aria-label="More actions" onClick={(e) => setMenu({ anchor: e.currentTarget, link })}>
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Box>

                <Box sx={{
                  display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1.25, flexWrap: 'wrap',
                  borderTop: '1px solid rgba(var(--color-text-rgb), 0.08)', bgcolor: 'rgba(var(--color-text-rgb), 0.02)',
                }}>
                  <Tooltip title="Total views: every time the link was opened, before or after the WhatsApp check">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'var(--color-text-muted)', fontSize: 13 }}>
                      <VisibilityIcon sx={{ fontSize: 16 }} /> {link.viewCount} <Box component="span" sx={{ opacity: 0.75 }}>Total</Box>
                    </Box>
                  </Tooltip>
                  <Tooltip title="Logged-in views: 3D map views by buyers who verified on WhatsApp">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'var(--color-text-muted)', fontSize: 13 }}>
                      <VisibilityIcon sx={{ fontSize: 16 }} /> {link.loggedInViewCount ?? 0} <Box component="span" sx={{ opacity: 0.75 }}>Logged In</Box>
                    </Box>
                  </Tooltip>
                  <Box component="button" type="button"
                    onClick={() => { logSharableLinkAction({ action: 'leads_open', link, source: 'card', leadCount: link.leadCount }); setLeadsFor(link); }}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 0.5, border: 0, bgcolor: 'transparent', cursor: 'pointer',
                      color: link.leadCount > 0 ? 'var(--color-accent)' : 'var(--color-text-muted)', fontSize: 13, fontWeight: 700, p: 0, font: 'inherit',
                    }}>
                    <PeopleIcon sx={{ fontSize: 16 }} /> {link.leadCount} {link.leadCount === 1 ? 'lead' : 'leads'}
                    {link.lastLeadAt && (
                      <Typography component="span" variant="caption" sx={{ color: 'var(--color-text-muted)', fontWeight: 400, ml: 0.5 }}>
                        · last {formatRelative(link.lastLeadAt)}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
                    {!revoked && (
                      <>
                        <Tooltip title="Copy link">
                          <IconButton size="small" aria-label="Copy link"
                            onClick={async () => {
                              logSharableLinkAction({ action: 'copy', link, source: 'card' });
                              setToast(await copyText(shareLinkUrl(link)) ? 'Link copied' : 'Copy failed');
                            }}>
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Share on WhatsApp">
                          <IconButton size="small" aria-label="Share on WhatsApp" aria-haspopup="menu"
                            onClick={(e) => setWaMenu({ anchor: e.currentTarget, link })}
                            sx={{ color: '#25d366' }}>
                            <WhatsAppIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
      </Box>

      <Menu anchorEl={menu?.anchor} open={!!menu} onClose={() => setMenu(null)}>
        <MenuItem onClick={() => {
          if (menu) {
            logSharableLinkAction({ action: 'leads_open', link: menu.link, source: 'menu', leadCount: menu.link.leadCount });
            setLeadsFor(menu.link);
          }
          setMenu(null);
        }}>
          <ListItemIcon><PeopleIcon fontSize="small" /></ListItemIcon>View leads
        </MenuItem>
        <MenuItem onClick={() => { if (menu) openConfig(menu.link); setMenu(null); }}>
          <ListItemIcon><TuneIcon fontSize="small" /></ListItemIcon>View configuration
        </MenuItem>
        {menu?.link.revokedAt && (
          <MenuItem onClick={() => { setRemoving(menu.link); setMenu(null); }} sx={{ color: 'var(--color-danger)' }}>
            <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: 'var(--color-danger)' }} /></ListItemIcon>Remove from view
          </MenuItem>
        )}
        {menu && !menu.link.revokedAt && [
          <MenuItem key="preview" onClick={() => { void handlePreview(menu.link); setMenu(null); }}>
            <ListItemIcon><OpenInNewIcon fontSize="small" /></ListItemIcon>Preview
          </MenuItem>,
          <MenuItem key="qr" onClick={() => {
            logSharableLinkAction({ action: 'qr_open', link: menu.link, source: 'menu' });
            setQrFor(menu.link);
            setMenu(null);
          }}>
            <ListItemIcon><QrCodeIcon fontSize="small" /></ListItemIcon>Show QR code
          </MenuItem>,
          <MenuItem key="revoke" onClick={() => { setAlsoRemove(false); setRevoking(menu.link); setMenu(null); }} sx={{ color: 'var(--color-danger)' }}>
            <ListItemIcon><LinkOffIcon fontSize="small" sx={{ color: 'var(--color-danger)' }} /></ListItemIcon>Revoke link
          </MenuItem>,
        ]}
      </Menu>

      <Menu anchorEl={waMenu?.anchor} open={!!waMenu} onClose={() => setWaMenu(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        MenuListProps={{ sx: { py: 0.5 } }} PaperProps={{ sx: { minWidth: 260, maxWidth: 'calc(100vw - 32px)' } }}>
        <MenuItem sx={{ py: 1.25 }} onClick={() => { if (waMenu) setSendToClientsFor(waMenu.link); setWaMenu(null); }}>
          <ListItemIcon><GroupsIcon sx={{ color: 'var(--color-accent)' }} /></ListItemIcon>
          <ListItemText primary="Send to my EOI/Clients" secondary="Powered by Super Connector"
            primaryTypographyProps={{ fontWeight: 600 }} secondaryTypographyProps={{ fontSize: 12 }} />
        </MenuItem>
        {waMenu && (
          <MenuItem component="a" href={whatsAppShareUrl(waMenu.link)} target="_blank" rel="noopener" sx={{ py: 1.25 }}
            onClick={() => { logSharableLinkAction({ action: 'whatsapp', link: waMenu.link, source: 'card' }); setWaMenu(null); }}>
            <ListItemIcon><PersonSearchIcon sx={{ color: '#25d366' }} /></ListItemIcon>
            <ListItemText primary="Choose a WhatsApp contact" secondary="Pick anyone in WhatsApp"
              primaryTypographyProps={{ fontWeight: 600 }} secondaryTypographyProps={{ fontSize: 12 }} />
          </MenuItem>
        )}
      </Menu>
      <ShareLinkToClientsSheet link={sendToClientsFor} onClose={() => setSendToClientsFor(null)} />

      <Dialog open={!!revoking} onClose={revokeBusy ? undefined : () => setRevoking(null)}>
        <DialogTitle>Revoke this link?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Anyone opening it will see a "link no longer active" page with your contact details. Leads you
            already have stay in Super Connector. This can't be undone.
          </DialogContentText>
          <FormControlLabel
            sx={{ mt: 1.5 }}
            control={<Checkbox checked={alsoRemove} onChange={(e) => setAlsoRemove(e.target.checked)} disabled={revokeBusy} />}
            label="Also remove it from My Sharable Links"
          />
          {alsoRemove && revoking && <RemoveWarning link={revoking} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevoking(null)} disabled={revokeBusy}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleRevoke} disabled={revokeBusy}
            startIcon={revokeBusy ? <CircularProgress size={16} color="inherit" /> : undefined}>
            {alsoRemove ? 'Revoke & remove' : 'Revoke'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!removing} onClose={removeBusy ? undefined : () => setRemoving(null)}>
        <DialogTitle>Remove this link from view?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {removing ? <><strong>{removing.projectName || removing.projectId}</strong>{removing.description ? ` · ${removing.description}` : ''}</> : null}
          </DialogContentText>
          {removing && <RemoveWarning link={removing} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoving(null)} disabled={removeBusy}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleRemove} disabled={removeBusy}
            startIcon={removeBusy ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>


      <CreateShareLinkDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(link) => setLinks((prev) => [link, ...(prev ?? [])])}
        featuresUnlocked={unlocked !== false}
        onLockedFeature={promptUnlock}
      />
      {unlockTemplate && (
        <DistinctiveDialog
          open
          onClose={() => setUnlockTemplate(null)}
          values={distinctiveValues}
          templates={[unlockTemplate]}
          title="Unlock this block"
          description="Tap the design, then share it out once to unlock Price Promo, Thumbs-up, Valuation and Project Request."
          hideInterestActions
          onShared={() => {
            setUnlocked(true);
            setToast('Blocks unlocked — close this to pick them');
          }}
        />
      )}
      {appWindow.element}
      <CreateShareLinkDialog open={!!qrFor} existingLink={qrFor} onClose={() => setQrFor(null)} />
      <CreateShareLinkDialog
        open={!!configFor}
        editLink={configFor}
        onClose={() => setConfigFor(null)}
        onSaved={(saved) => {
          setLinks((prev) => prev?.map((l) => (l.id === saved.id ? saved : l)) ?? prev);
          setToast('Changes saved');
        }}
      />
      <ShareLinkLeadsDrawer link={leadsFor} onClose={() => setLeadsFor(null)} />
      <Snackbar open={!!toast} autoHideDuration={2000} onClose={() => setToast(null)} message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
}
